import { useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { useUser } from '../context/UserContext';
import { clasificarRutsMontos } from '../api/apiConfig';
import { formatNumberEsCL } from '../utils/numberFormat';

const normalizeHeader = (v) =>
  String(v || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[_\-.]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const findCol = (headers, aliases) => {
  const set = new Set(aliases.map(normalizeHeader));
  return headers.find((h) => set.has(normalizeHeader(h))) || null;
};

const parseMonto = (v) => {
  if (v == null || v === '') return 0;
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  let raw = String(v).trim().replace(/\s+/g, '');
  if (!raw) return 0;
  const hasDot = raw.includes('.');
  const hasComma = raw.includes(',');
  if (hasDot && hasComma) {
    if (raw.lastIndexOf(',') > raw.lastIndexOf('.')) {
      raw = raw.replace(/\./g, '').replace(',', '.');
    } else {
      raw = raw.replace(/,/g, '');
    }
  } else if (hasComma) {
    raw = (raw.match(/,/g) || []).length > 1 ? raw.replace(/,/g, '') : raw.replace(',', '.');
  } else if (hasDot && (raw.match(/\./g) || []).length > 1) {
    raw = raw.replace(/\./g, '');
  }
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
};

const parseExcelToHojas = async (file) => {
  const data = await file.arrayBuffer();
  const wb = XLSX.read(data, { type: 'array' });
  const hojas = {};

  for (const sheetName of wb.SheetNames) {
    const sheet = wb.Sheets[sheetName];
    const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
    if (!rows.length) continue;

    const headers = Object.keys(rows[0] || {});
    const colRut =
      findCol(headers, ['RUT', 'R.CLI', 'RUT_CLIENTE', 'RUT_CLI', 'ETIQUETAS DE FILA']) ||
      headers[0];
    const colMonto =
      findCol(headers, ['MONTO', 'SALDO', 'SALDO_CAPITAL', 'CAPITAL']) ||
      headers.find((h) => normalizeHeader(h).includes('MONTO')) ||
      headers[1];

    const parsed = [];
    for (const row of rows) {
      const rut = row[colRut];
      const rutTxt = String(rut ?? '').trim().toUpperCase();
      if (!rutTxt || rutTxt === 'TOTAL' || rutTxt === 'TOTALES') continue;
      parsed.push({
        rut: rutTxt,
        monto: parseMonto(row[colMonto]),
      });
    }
    if (parsed.length) hojas[sheetName] = parsed;
  }
  return hojas;
};

const ClasificacionRuts = () => {
  const { user } = useUser();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [hoja, setHoja] = useState('');
  const [vista, setVista] = useState('sector');

  const hojasNombres = useMemo(
    () => (result?.meta?.hojas?.length ? result.meta.hojas : Object.keys(result?.resultados || {})),
    [result],
  );

  const hojaActiva = hoja || hojasNombres[0] || '';
  const payloadHoja = result?.resultados?.[hojaActiva] || null;

  const tabla = useMemo(() => {
    if (!payloadHoja) return [];
    if (vista === 'clasificacion') return payloadHoja.clasificacion || [];
    if (vista === 'region') return payloadHoja.region || [];
    if (vista === 'tamano') return payloadHoja.tamano || [];
    if (vista === 'detalle') return (payloadHoja.detalle || []).slice(0, 200);
    return payloadHoja.sector || [];
  }, [payloadHoja, vista]);

  const columnas = useMemo(() => {
    if (!tabla.length) return [];
    return Object.keys(tabla[0]);
  }, [tabla]);

  const procesar = async () => {
    if (!file) {
      setError('Selecciona un Excel con RUT y montos.');
      return;
    }
    try {
      setLoading(true);
      setError('');
      setResult(null);
      const hojas = await parseExcelToHojas(file);
      if (!Object.keys(hojas).length) {
        throw new Error('No se encontraron filas con RUT válido en el Excel.');
      }
      const resp = await clasificarRutsMontos(user, {
        op: 'procesar',
        nombre_archivo: file.name,
        hojas,
        // Igual que Control Infraestructura: solo clasifica en memoria; no crea tablas.
        guardar: false,
      });
      if (!resp?.ok) {
        throw new Error(resp?.error || 'La clasificación falló.');
      }
      setResult(resp);
      setHoja(resp?.meta?.hojas?.[0] || Object.keys(resp?.resultados || {})[0] || '');
      setVista('sector');
    } catch (e) {
      console.error(e);
      setError(e?.message || 'No se pudo clasificar el archivo.');
    } finally {
      setLoading(false);
    }
  };

  const exportarExcel = () => {
    if (!result?.resultados) return;
    const wb = XLSX.utils.book_new();
    Object.entries(result.resultados).forEach(([name, payload]) => {
      const detalle = payload.detalle || [];
      const ws = XLSX.utils.json_to_sheet(detalle);
      const start = detalle.length + 3;
      XLSX.utils.sheet_add_json(ws, payload.sector || [], { origin: `A${start}` });
      XLSX.utils.sheet_add_json(ws, payload.clasificacion || [], {
        origin: `A${start + (payload.sector?.length || 0) + 2}`,
      });
      XLSX.utils.sheet_add_json(ws, payload.region || [], { origin: `F${start}` });
      XLSX.utils.sheet_add_json(ws, payload.tamano || [], { origin: `K${start}` });
      XLSX.utils.book_append_sheet(wb, ws, String(name).slice(0, 31) || 'Hoja');
    });
    XLSX.writeFile(wb, `${(result.meta?.nombre_archivo || 'ruts_montos').replace(/\.xlsx?$/i, '')}_clasificado.xlsx`);
  };

  const isNumericCol = (col) => {
    const s = String(col || '').toLowerCase();
    return s === 'monto' || s === 'clientes' || s.includes('monto') || s.includes('saldo');
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-800 mb-2">Clasificación RUTs</h1>
      <p className="text-gray-600 mb-6 max-w-3xl">
        Sube un Excel con RUTs y montos para clasificarlos por sector económico, clasificación,
        región y tamaño de empresa.
      </p>

      <div className="seccion-datos mb-6 max-w-3xl p-6 md:p-8">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Carga del archivo</h2>

        <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700 mb-6">
          <li>
            Prepara un Excel (<code>.xlsx</code>) con una hoja por producto:{' '}
            <strong>crédito</strong>, <strong>leasing</strong> y/o <strong>factoring</strong>.
          </li>
          <li>
            En cada hoja incluye al menos dos columnas: <strong>RUT</strong> y{' '}
            <strong>Monto</strong> (o Saldo / Capital).
          </li>
          <li>Selecciona el archivo y pulsa <strong>Clasificar</strong>.</li>
          <li>
            Revisa los resultados abajo y, si quieres, descarga el Excel clasificado.
          </li>
        </ol>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Archivo Excel
            </label>
            <input
              type="file"
              accept=".xlsx,.xls"
              className="block w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-slate-100 file:text-slate-800 hover:file:bg-slate-200"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
            {file ? (
              <p className="mt-2 text-sm text-gray-600">Seleccionado: {file.name}</p>
            ) : (
              <p className="mt-2 text-sm text-gray-400">Ningún archivo seleccionado.</p>
            )}
          </div>

          <button
            type="button"
            className="btn-primary w-full py-3 text-base disabled:opacity-50"
            disabled={loading || !file}
            onClick={procesar}
          >
            {loading ? 'Clasificando…' : 'Clasificar'}
          </button>
          {error ? <div className="text-sm text-red-600">{error}</div> : null}
        </div>
      </div>

      {result?.resultados ? (
        <div className="seccion-datos mb-6">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {hojasNombres.map((h) => (
              <button
                key={h}
                type="button"
                onClick={() => setHoja(h)}
                className={`px-3 py-1 rounded text-sm ${
                  h === hojaActiva ? 'bg-slate-700 text-white' : 'bg-gray-100 text-gray-700'
                }`}
              >
                {h}
              </button>
            ))}
            <button type="button" className="btn-primary text-sm ml-auto" onClick={exportarExcel}>
              Exportar Excel
            </button>
          </div>
          <div className="flex flex-wrap gap-2 mb-4 border-b border-gray-200 pb-2">
            {[
              ['sector', 'Sector económico'],
              ['clasificacion', 'Clasificación'],
              ['region', 'Región'],
              ['tamano', 'Tamaño empresa'],
              ['detalle', 'Detalle (200)'],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setVista(key)}
                className={`px-3 py-1 text-sm ${
                  vista === key
                    ? 'border-b-2 border-blue-600 text-blue-700 font-semibold'
                    : 'text-gray-500'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="overflow-auto max-h-[520px] border border-gray-200 rounded">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  {columnas.map((c) => (
                    <th key={c} className="px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tabla.map((row, idx) => (
                  <tr key={idx} className="border-t border-gray-100">
                    {columnas.map((c) => {
                      const val = row[c];
                      const show =
                        val == null
                          ? '—'
                          : isNumericCol(c)
                            ? formatNumberEsCL(val)
                            : String(val);
                      return (
                        <td
                          key={c}
                          className={`px-3 py-1.5 whitespace-nowrap ${isNumericCol(c) ? 'text-right' : ''}`}
                        >
                          {show}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {!tabla.length ? (
                  <tr>
                    <td className="px-3 py-6 text-center text-gray-400" colSpan={columnas.length || 1}>
                      Sin datos
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ClasificacionRuts;
